import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { getAbhaLinkPreview, linkABHAToPatient } from '../../services/api/abhaApi';
import { buildAbhaLinkRequest } from '../../services/api/abhaProfile';
import { formatDateIST } from '../../utils/formatIST';
import { LINK_CONFIRM } from '../../constants/abdmCopy';

/**
 * AbhaLinkConfirmDialog — "Is this the same person?" gate shown BEFORE an ABHA is linked to an
 * EXISTING patient record. Every place that links an ABHA to an existing patient goes through this.
 *
 * The server decides (link-preview + link): the "Yes" button only exists when the date of birth
 * matches exactly (or the patient has just said it correctly). The ABHA date of birth is never
 * shown until it matches.
 *
 * Props:
 *   patientId   — existing patient to link to
 *   snapshot    — verified ABHA profile snapshot (what gets sent to the link endpoint)
 *   onLinked    — (result) => void, after "Done" on the success screen
 *   onRegisterNew — ({ oldUhid }) => void, "Register as new patient"
 *   onCancel    — () => void
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "1980-03-14" → "14 Mar 1980" (no Date object: no timezone shift). */
export function formatIsoDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return '';
  return `${m[3]} ${MONTHS[Number(m[2]) - 1] || ''} ${m[1]}`;
}

/** "14/03/1980" → "1980-03-14", or null when not a real, non-future date. */
export function parseDobInput(text) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text || '');
  if (!m) return null;
  const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  if (dt > new Date()) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

const formatTyping = (raw) => {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const ABDM_NOTE = LINK_CONFIRM.note;
const NEW_PATIENT_NOTE = "Date of birth or gender doesn't match, so this ABHA will be linked to a new patient with a new UHID. The old record is not changed.";
const YEAR_ONLY_NOTE = "This ABHA has only a birth year, so it can't be linked to an existing record. Register as a new patient to link it.";
const BLOCKED_COPY = {
  GENDER_MISMATCH: "Gender doesn't match, so this ABHA will be linked to a new patient with a new UHID. The old record is not changed.",
  TOO_MANY_ATTEMPTS: 'Too many wrong tries, so this ABHA will be linked to a new patient with a new UHID. The old record is not changed.',
  ABHA_DOB_UNAVAILABLE: YEAR_ONLY_NOTE,
  DOB_MISMATCH: "Date of birth or gender doesn't match, so this ABHA will be linked to a new patient with a new UHID. The old record is not changed.",
};

function Row({ label, value, mark, diff, muted, isDark }) {
  const tones = {
    ok: { background: '#dcfce7', color: '#15803d', border: '#86efac', text: '✓' },
    df: { background: '#fef3c7', color: '#b45309', border: '#f59e0b', text: '!' },
    sp: { background: '#f1f5f9', color: '#475569', border: '#94a3b8', text: '~' },
  };
  const t = mark ? tones[mark] : null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'center',
      padding: '0.5rem 0.875rem',
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
      background: diff ? (isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb') : 'transparent',
    }}>
      <span style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#475569' }}>{label}</span>
      <span style={{
        fontWeight: muted ? 400 : 600, fontStyle: muted ? 'italic' : 'normal', textAlign: 'right',
        color: muted ? (isDark ? '#9ca3af' : '#64748b') : 'inherit', fontSize: '0.8125rem',
      }}>
        {value}
        {t && (
          <span aria-hidden="true" style={{
            display: 'inline-block', width: 18, height: 18, borderRadius: '50%', textAlign: 'center',
            fontSize: 11, lineHeight: '18px', marginLeft: 6, fontWeight: 700,
            background: t.background, color: t.color, border: `1px solid ${t.border}`,
          }}>{t.text}</span>
        )}
      </span>
    </div>
  );
}

function Card({ title, teal, children, isDark }) {
  return (
    <div style={{
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1'}`, borderRadius: '0.75rem', overflow: 'hidden',
      background: isDark ? '#0f172a' : '#fff', flex: '1 1 14rem', minWidth: 0,
    }}>
      <h3 style={{
        margin: 0, padding: '0.5rem 0.875rem', fontSize: '0.8125rem', fontWeight: 700,
        background: teal ? (isDark ? 'rgba(13,148,136,0.25)' : '#ccfbf1') : (isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
        color: teal ? (isDark ? '#5eead4' : '#0f766e') : 'inherit',
        borderBottom: `1px solid ${teal ? '#5eead4' : (isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1')}`,
      }}>{title}</h3>
      {children}
    </div>
  );
}

function Note({ tone, children, isDark }) {
  const tones = {
    info: isDark ? { bg: 'rgba(13,148,136,0.15)', bd: '#115e59', fg: '#99f6e4' } : { bg: '#f0fdfa', bd: '#5eead4', fg: '#115e59' },
    warn: isDark ? { bg: 'rgba(245,158,11,0.15)', bd: '#b45309', fg: '#fde68a' } : { bg: '#fffbeb', bd: '#f59e0b', fg: '#92400e' },
    neu: isDark ? { bg: 'rgba(255,255,255,0.06)', bd: 'rgba(255,255,255,0.15)', fg: '#d1d5db' } : { bg: '#f1f5f9', bd: '#cbd5e1', fg: '#334155' },
    err: isDark ? { bg: '#3b1518', bd: '#b91c1c', fg: '#fecaca' } : { bg: '#fef2f2', bd: '#fca5a5', fg: '#991b1b' },
  }[tone];
  return (
    <div style={{
      borderRadius: '0.625rem', padding: '0.625rem 0.875rem', marginTop: '0.75rem', fontSize: '0.8125rem',
      background: tones.bg, border: `1px solid ${tones.bd}`, color: tones.fg,
    }}>{children}</div>
  );
}

export default function AbhaLinkConfirmDialog({ patientId, snapshot, onLinked, onRegisterNew, onCancel, onVerifyAgain }) {
  const { isDark } = useTheme();
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [dobText, setDobText] = useState('');
  const [triesLeft, setTriesLeft] = useState(0);
  // Server verdict from a link attempt that overrides the preview: 'ask' | 'nomatch' | 'blocked' | 'already'
  const [forced, setForced] = useState(null);
  const [blockedCode, setBlockedCode] = useState('DOB_MISMATCH');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);   // single-flight: a fast double-click can't send two link calls
  const [error, setError] = useState('');
  const abhaNumber = snapshot?.abhaNumber;
  const [done, setDone] = useState(null);   // { result, statedDob }

  // ONE preview fetch per dialog open (keyed on the patient, a primitive).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getAbhaLinkPreview({ patientId, abhaNumber });
        if (cancelled) return;
        setPreview(p);
        setTriesLeft(p.triesLeft);
      } catch (e) {
        if (cancelled) return;
        if (e?.linkCode === 'ABHA_SESSION_EXPIRED') setForced('expired');
        else if (e?.linkCode === 'PATIENT_NOT_FOUND') setLoadError('This patient record was not found.');
        else setLoadError(e?.message || 'Could not check this patient. Please try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [patientId, abhaNumber]);

  const attemptLink = useCallback(async (statedDob) => {
    const req = buildAbhaLinkRequest(patientId, snapshot);
    if (!req) { setError('This ABHA cannot be linked.'); return; }
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await linkABHAToPatient({ ...req, ...(statedDob ? { statedDob } : {}) });
      setDone({ result, statedDob: statedDob || null });
    } catch (e) {
      switch (e?.linkCode) {
        case 'DOB_MISMATCH': {
          const left = Number(e.triesLeft) || 0;
          setTriesLeft(left);
          setBlockedCode('DOB_MISMATCH');
          setForced(left > 0 ? 'nomatch' : 'blocked');
          break;
        }
        case 'DOB_REQUIRED': setForced('ask'); break;
        case 'ABHA_SESSION_EXPIRED': setForced('expired'); break;
        case 'ABHA_ALREADY_LINKED': setForced('already'); break;
        case 'GENDER_MISMATCH':
        case 'TOO_MANY_ATTEMPTS':
        case 'ABHA_DOB_UNAVAILABLE':
          setBlockedCode(e.linkCode);
          setForced('blocked');
          break;
        default:
          setError(e?.message || 'Could not link the ABHA. Please try again.');
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [patientId, snapshot]);

  // Escape cancels, except once linked (the link is done; use Done).
  useEffect(() => {
    if (done || busy) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;   // Escape while typing must not close the dialog
      onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [done, busy, onCancel]);

  // ── what to show ──
  let mode = 'loading';
  if (done) mode = 'done';
  else if (forced === 'expired') mode = 'expired';
  else if (loadError) mode = 'loadError';
  else if (preview) {
    if (forced) mode = forced;
    else if (preview.alreadyLinkedElsewhere) mode = 'already';
    else if (preview.dobStatus === 'MATCH' && preview.genderMatches !== false) mode = 'match';
    else if (preview.dobStatus === 'NOT_RECORDED' && triesLeft > 0) mode = 'ask';
    else mode = 'blocked';
  }

  const record = preview?.record;
  const abha = preview?.abha;
  // The ABHA's own DOB is never displayed (it would leak a guessed DOB) — a match shows only a tick.
  const showAbhaDob = mode === 'match';
  const parsedDob = parseDobInput(dobText);
  const matchOk = mode === 'match';
  const registerNew = () => onRegisterNew?.({ oldUhid: record?.uhid || '' });

  const fieldSide = (isRecord) => {
    const s = isRecord ? record : abha;
    const dobValue = isRecord
      ? (record.dob ? formatIsoDate(record.dob) : 'Not recorded')
      : (showAbhaDob ? 'Matches our record'
        : mode === 'ask' || mode === 'nomatch' ? 'Hidden until entered'
          : preview.dobStatus === 'ABHA_YEAR_ONLY' ? 'Year only' : 'Different');
    const dobMuted = isRecord ? !record.dob : !showAbhaDob;
    const dobMark = matchOk ? 'ok' : (mode === 'blocked' && preview.dobStatus !== 'NOT_RECORDED' ? 'df' : undefined);
    const genderMark = matchOk && preview.genderMatches === true ? 'ok' : (preview.genderMatches === false && mode !== 'ask' ? 'df' : undefined);
    const nameMark = matchOk && preview.nameDiffers ? 'sp' : undefined;
    return (
      <>
        <Row isDark={isDark} label={isRecord ? 'Name (as typed)' : 'Full name'} value={s.name} mark={nameMark} />
        <Row isDark={isDark} label="Date of birth" value={dobValue} muted={dobMuted} mark={dobMark}
          diff={dobMark === 'df'} />
        <Row isDark={isDark} label="Gender" value={s.gender} mark={genderMark} diff={genderMark === 'df'} />
        {isRecord ? (
          <>
            <Row isDark={isDark} label="UHID" value={record.uhid} />
            <Row isDark={isDark} label="Mobile" value={record.mobile_masked} />
            <Row isDark={isDark} label="Last visit" value={record.last_visit_date ? formatIsoDate(record.last_visit_date) : 'None yet'} />
            <Row isDark={isDark} label="Past visits" value={String(record.visit_count ?? 0)} />
          </>
        ) : (
          <>
            <Row isDark={isDark} label="ABHA number" value={abha.abha_number_masked} />
            <Row isDark={isDark} label="ABHA address" value={abha.abha_address} />
          </>
        )}
      </>
    );
  };

  const comparison = preview && (
    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
      <Card isDark={isDark} title="Our record">{fieldSide(true)}</Card>
      <Card isDark={isDark} teal title={LINK_CONFIRM.cardTitle}>{fieldSide(false)}</Card>
    </div>
  );

  const btn = (primary) => ({
    font: 'inherit', fontWeight: 600, padding: '0.6875rem 1rem', borderRadius: '0.625rem',
    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
    border: `1px solid ${primary ? '#0f766e' : '#0d9488'}`,
    background: primary ? '#0d9488' : (isDark ? 'transparent' : '#fff'),
    color: primary ? '#fff' : (isDark ? '#5eead4' : '#0f766e'),
  });
  const linkBtn = {
    background: 'none', border: 0, color: isDark ? '#9ca3af' : '#475569', textDecoration: 'underline',
    padding: '0.6875rem 0.5rem', font: 'inherit', cursor: busy ? 'not-allowed' : 'pointer',
  };
  const cancel = <button type="button" style={linkBtn} disabled={busy} onClick={onCancel}>Cancel</button>;
  const btnRow = { display: 'flex', gap: '0.625rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.875rem' };

  let title = 'Is this the same person?';
  let hint = 'Check the details below before linking the ABHA.';
  if (mode === 'match') hint = 'Date of birth matches exactly. Check the details below before linking the ABHA.';
  if (mode === 'ask') { title = "Confirm it's the same person"; hint = 'Our record has no date of birth. Ask the patient to say it.'; }
  if (mode === 'nomatch' || mode === 'blocked') { title = 'This does not look like the same person'; hint = ''; }
  if (mode === 'already') { title = 'This ABHA is already linked'; hint = ''; }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm same person"
      style={{
        position: 'fixed', inset: 0, zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', padding: '1rem', overflowY: 'auto',
      }}
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget || busy) return;
        if (done) onLinked?.(done.result);   // already linked: closing must still report it
        else onCancel?.();
      }}
    >
      <div style={{
        width: '100%', maxWidth: '44rem', maxHeight: '92vh', overflowY: 'auto', padding: '1.25rem',
        background: isDark ? '#1e293b' : '#fff', color: isDark ? '#f5f5f7' : '#0f172a',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1'}`, borderRadius: '1rem',
        boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
      }}>
        {mode === 'loading' && <p style={{ margin: 0 }}>Checking the patient record…</p>}

        {mode === 'loadError' && (
          <>
            <Note tone="err" isDark={isDark}>{loadError}</Note>
            <div style={btnRow}>{cancel}</div>
          </>
        )}

        {mode === 'expired' && (
          <>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>ABHA verification expired</h2>
            <Note tone="warn" isDark={isDark}>ABHA verification has expired. Please verify again.</Note>
            <div style={btnRow}>
              <button type="button" style={btn(true)} onClick={() => (onVerifyAgain || onCancel)?.()}>Verify again</button>
              {cancel}
            </div>
          </>
        )}

        {mode === 'already' && (
          <>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>{title}</h2>
            <Note tone="warn" isDark={isDark}>
              This ABHA is already linked to another patient, so it cannot be linked here.
            </Note>
            <div style={btnRow}>{cancel}</div>
          </>
        )}

        {mode === 'done' && (() => {
          const { result, statedDob } = done;
          const dob = record.dob || statedDob || '';
          const shownName = result.nameUpdated ? abha.name : record.name;
          const linkedOn = formatDateIST(new Date().toISOString());   // IST, not the device clock
          return (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{shownName}</h2>
                  {result.nameUpdated && (
                    <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                      Also known as: <span style={{ border: '1px solid #cbd5e1', borderRadius: 999, padding: '2px 10px' }}>{record.name}</span>
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', marginTop: 6 }}>
                    {[record.uhid, record.gender, dob ? `b. ${formatIsoDate(dob)}` : '', record.mobile_masked].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.75rem' }}>
                  <span style={{
                    display: 'inline-block', background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                    borderRadius: 999, padding: '3px 10px', fontWeight: 600,
                  }}>ABHA linked ✓</span>
                  <div style={{ marginTop: 6 }}>{abha.abha_number_masked}<br />Linked on {linkedOn}</div>
                </div>
              </div>
              <div style={btnRow}>
                <button type="button" style={btn(true)} onClick={() => onLinked?.(result)}>Done</button>
              </div>
            </>
          );
        })()}

        {['match', 'ask', 'nomatch', 'blocked'].includes(mode) && (
          <>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>{title}</h2>
            {hint && <p style={{ margin: '0 0 0.875rem', color: isDark ? '#9ca3af' : '#475569' }}>{hint}</p>}
            {comparison}

            {mode === 'match' && (
              <>
                {preview.nameDiffers && (
                  <Note tone="neu" isDark={isDark}>
                    <b>Spelling differs.</b> The date of birth matches exactly, so this can be the same person.
                    The name on record will change to &quot;{abha.name}&quot;. The old spelling &quot;{record.name}&quot; stays
                    searchable as &quot;also known as&quot;.
                  </Note>
                )}
                <div style={{ fontWeight: 700, fontSize: '1rem', margin: '1rem 0 0.625rem' }}>Is this the same person?</div>
                <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button type="button" style={btn(true)} disabled={busy} onClick={() => attemptLink()}>
                    Yes, same person — link ABHA
                  </button>
                  <button type="button" style={btn(false)} disabled={busy} onClick={registerNew}>
                    No, different person — register as new patient
                  </button>
                  {cancel}
                </div>
                <Note tone="info" isDark={isDark}>{ABDM_NOTE}</Note>
              </>
            )}

            {(mode === 'ask' || mode === 'nomatch') && (
              <>
                {mode === 'ask' && (
                  <div style={{ fontWeight: 700, fontSize: '1rem', margin: '1rem 0 0.625rem' }}>
                    Ask the patient: What is your date of birth as on your Aadhaar?
                  </div>
                )}
                <div style={{ maxWidth: '22rem', marginTop: mode === 'ask' ? 0 : '0.875rem' }}>
                  <label htmlFor="abha-stated-dob" style={{ display: 'block', fontSize: '0.75rem', marginBottom: 4, color: isDark ? '#9ca3af' : '#475569' }}>
                    Date of birth as on Aadhaar
                  </label>
                  <input
                    id="abha-stated-dob"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="DD/MM/YYYY"
                    value={dobText}
                    maxLength={10}
                    disabled={busy}
                    onChange={(e) => setDobText(formatTyping(e.target.value))}
                    style={{
                      width: '100%', padding: '0.5625rem 0.75rem', borderRadius: '0.5rem', font: 'inherit',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8'}`,
                      background: isDark ? '#0f172a' : '#fff', color: 'inherit', boxSizing: 'border-box',
                    }}
                  />
                </div>
                {mode === 'nomatch' && (
                  <Note tone="warn" isDark={isDark}>
                    <b>Date of birth doesn&apos;t match</b>, so this ABHA can be linked to a new patient with a new UHID.
                    The old record is not changed.
                  </Note>
                )}
                <div style={btnRow}>
                  <button type="button" style={btn(true)} disabled={busy || !parsedDob} onClick={() => attemptLink(parsedDob)}>
                    {busy ? 'Checking…' : 'Check'}
                  </button>
                  {mode === 'nomatch' && (
                    <button type="button" style={btn(false)} disabled={busy} onClick={registerNew}>Register as new patient</button>
                  )}
                  {cancel}
                  {mode === 'nomatch' && (
                    <button type="button" style={{ ...linkBtn, color: isDark ? '#5eead4' : '#0f766e' }} disabled={busy}
                      onClick={() => { setDobText(''); setForced('ask'); }}>
                      {`Try again (${triesLeft} left)`}
                    </button>
                  )}
                </div>
                <p style={{ fontSize: '0.75rem', margin: '0.625rem 0 0', color: isDark ? '#9ca3af' : '#475569' }}>
                  {`${triesLeft} ${triesLeft === 1 ? 'try' : 'tries'} allowed.`}
                </p>
              </>
            )}

            {mode === 'blocked' && (
              <>
                <Note tone="warn" isDark={isDark}>
                  {forced ? BLOCKED_COPY[blockedCode]
                    : preview.dobStatus === 'NOT_RECORDED' ? BLOCKED_COPY.TOO_MANY_ATTEMPTS
                    : (preview.dobStatus === 'ABHA_YEAR_ONLY' || preview.dobStatus === 'ABHA_DOB_UNAVAILABLE'
                      ? YEAR_ONLY_NOTE : NEW_PATIENT_NOTE)}
                </Note>
                <div style={btnRow}>
                  <button type="button" style={btn(true)} disabled={busy} onClick={registerNew}>Register as new patient</button>
                  {cancel}
                </div>
              </>
            )}

            {error && <Note tone="err" isDark={isDark}>{error}</Note>}
          </>
        )}
      </div>
    </div>
  );
}
